/**
 * Structured logging for auth-related events.
 *
 * Emits JSON-shaped entries with a stable `event` key and an `auth_event_id`
 * correlation id so proxy / refresh / API failures can be joined in log search.
 *
 * Kept minimal on purpose — no vendor lock-in. Downstream log processing
 * (Vercel Logs, Sentry, Datadog) can parse the JSON directly.
 */

export const AUTH_EVENT_ID_HEADER = "x-auth-event-id";

export type AuthErrorShape = {
  name?: string;
  code?: string;
  cause_code?: string;
  status?: number;
  message?: string;
  stack_preview?: string;
  body?: string;
  classified_as_refresh_token_error?: boolean;
};

export type AuthLogPayload = {
  event: string;
  auth_event_id: string;
  [key: string]: unknown;
};

export function newAuthEventId(): string {
  try {
    return globalThis.crypto?.randomUUID?.() ?? fallbackId();
  } catch {
    return fallbackId();
  }
}

function fallbackId(): string {
  return `ae_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function extractErrorShape(error: unknown): AuthErrorShape {
  if (!error || typeof error !== "object") {
    return { message: String(error) };
  }

  const err = error as Record<string, unknown>;
  const cause = err.cause as Record<string, unknown> | undefined;
  const stack = typeof err.stack === "string" ? err.stack : undefined;

  const shape: AuthErrorShape = {};
  if (typeof err.name === "string") shape.name = err.name;
  if (typeof err.code === "string") shape.code = err.code;
  if (cause && typeof cause.code === "string") shape.cause_code = cause.code;
  if (typeof err.status === "number") shape.status = err.status;
  if (typeof err.message === "string") shape.message = err.message;
  if (stack) shape.stack_preview = stack.split("\n").slice(0, 5).join("\n");

  const body = extractErrorBody(err, cause);
  if (body !== undefined) shape.body = body;

  return shape;
}

function extractErrorBody(
  err: Record<string, unknown>,
  cause: Record<string, unknown> | undefined
): string | undefined {
  const candidates: unknown[] = [err.body, err.response, cause?.body, cause?.response];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length > 0) {
      return candidate.slice(0, 500);
    }
    if (candidate && typeof candidate === "object") {
      try {
        return JSON.stringify(candidate).slice(0, 500);
      } catch {
        // ignore unserializable
      }
    }
  }
  return undefined;
}

const IS_BROWSER = globalThis.window !== undefined;

export function logAuthEvent(payload: AuthLogPayload): void {
  if (IS_BROWSER) {
    // In the browser we route to PostHog so we don't spam users' devtools
    // console. PostHog is initialized client-side in (customers)/providers.tsx
    // and attaches a singleton to window.
    void sendToPostHog(payload);
    return;
  }

  // Server-side: structured log line to Vercel Logs. Greppable by
  // `[auth_event]` prefix; parse the JSON for per-field search.
  try {
    console.info(`[auth_event] ${JSON.stringify(payload)}`);
  } catch {
    console.info(`[auth_event] ${payload.event} ${payload.auth_event_id}`);
  }
}

async function sendToPostHog(payload: AuthLogPayload): Promise<void> {
  try {
    const { getBrowserPostHog } = await import("@/lib/posthog-browser");
    const posthog = getBrowserPostHog();
    if (!posthog) return;
    // Flatten the nested `error` object so PostHog's property search works
    // on individual fields (`error_code`, `error_cause_code`, ...).
    const { event, error, ...rest } = payload;
    const properties: Record<string, unknown> = { ...rest };
    if (error && typeof error === "object" && !Array.isArray(error)) {
      for (const [key, value] of Object.entries(error)) {
        properties[`error_${key}`] = value;
      }
    } else if (error !== undefined) {
      properties.error = error;
    }
    posthog.capture(`auth_event.${event}`, properties);
  } catch {
    // PostHog unavailable (blocked, init failed, etc.) — silently drop.
    // We deliberately don't fall back to console.info since the whole point
    // of the PostHog path is to keep devtools clean.
  }
}
